import { IDeviceStatus } from '../interfaces/types.ts'
import { DateTime } from 'luxon'
import { effectiveTimezone } from '../store/authState.ts'

export const WakeReasons: string[] = [
    'Sleep canceled due to power cycle',
    'Wake from one time sleep',
    'Sleep canceled by user',
    'Wake from repeat sleep',
    'Sleep canceled due to low battery',
]

export const getWakeReasonText = (wakeReason?: string | number | null): string | null => {
    if (wakeReason === undefined || wakeReason === null) return null
    const index = typeof wakeReason === 'string' ? parseInt(wakeReason, 10) : wakeReason
    if (isNaN(index) || index < 0 || index >= WakeReasons.length) return null
    return WakeReasons[index]
}

export const hasScheduledSleep = (status: IDeviceStatus): boolean => {
    return status.sleepStart > 0 && status.sleepDuration > 0
}

export const isSleepDisabled = (status: IDeviceStatus): boolean => {
    return !status.powerOn || status.sleeping
}

export const isSleepFeatureEnabled = (account: any): boolean => {
    const envEnabled = import.meta.env.VITE_ENABLE_SLEEP_MODE === 'true'
    return envEnabled || account?.useAdvancedFeatures === true
}

export const isLedFeatureEnabled = (): boolean => {
    return import.meta.env.VITE_ENABLE_LED_CONTROL === 'true'
}

export const isAdvancedSettingsEnabled = (): boolean => {
    return import.meta.env.VITE_ENABLE_ADVANCED_SETTINGS === 'true'
}

export const calculateDurationMinutes = (startTime: number, endTime: number): number => {
    if (endTime > startTime) {
        return (endTime - startTime) / 60
    }
    // Handle overnight wrap-around (24h = 86400 seconds)
    return (86400 - startTime + endTime) / 60
}

export const calculateEndTime = (sleepStart: number, sleepDuration: number): number => {
    // sleepDuration is in minutes, sleepStart is in seconds
    return sleepStart + sleepDuration * 60
}

/**
 * Convert form time strings (HH:MM 24h) to device sleep settings.
 * Both one-time and repeat use the same format:
 *   startEpoch = full unix timestamp for the first sleep occurrence
 *   duration = sleep duration in minutes
 * The only difference is the repeat flag sent alongside.
 * Uses the effective timezone (browser local when override is active, account timezone otherwise).
 */
export const convertToSleepCommand = (startTime: string, endTime: string, _repeat: boolean): { startEpoch: number, duration: number } => {
    const zone = effectiveTimezone().value
    const [startH, startM] = startTime.split(':').map(Number)
    const [endH, endM] = endTime.split(':').map(Number)

    const now = DateTime.now().setZone(zone)
    let startDt = now.set({ hour: startH, minute: startM, second: 0, millisecond: 0 })
    let endDt = now.set({ hour: endH, minute: endM, second: 0, millisecond: 0 })

    // If start time has already passed today, schedule for tomorrow
    if (startDt <= now) {
        startDt = startDt.plus({ days: 1 })
        endDt = endDt.plus({ days: 1 })
    }

    // If end is before or equal to start, end is next day
    if (endDt <= startDt) {
        endDt = endDt.plus({ days: 1 })
    }

    const startEpoch = Math.floor(startDt.toSeconds())
    const duration = Math.floor((endDt.toSeconds() - startDt.toSeconds()) / 60)
    return { startEpoch, duration }
}
